import { useMemo, useEffect, useState } from 'react';
import { PHASE_PRODUCTION_BUILD } from 'next/constants';
import { useTimeout } from 'usehooks-ts';

import { fetchJson } from '~/lib/fetch';
import { useOneTap } from '~/lib/script';
import useUser from '~/lib/user';
import { useCollections } from '~/hooks/collections';

import { Loader } from '~/components/ui';
import { SpotlightCallout } from '~/components/smartcollections/components/SpotlightCallout';
import { Spotlight } from '~/components/smartcollections/components/Spotlight';
import { CountdownClock } from '~/components/smartcollections/components/CountdownClock';
import { ThematicRoadblock } from '~/components/smartcollections/roadblocks/ThematicRoadblock';
import ProductCarousel from '~/components/smartcollections/components/ProductCarousel';

export async function getStaticProps() {
  const isSSGBuild = process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD;

  // if this is a SSG build, we don't want to fetch data from the API
  // and let the first hit revalidate the data
  if (isSSGBuild) {
    return {
      props: {
        fallbackData: [],
      },
      revalidate: 1,
    };
  }

  try {
    const collections = await fetchJson(
      '/api/smartcollections/personal/?include_deals=true',
    );
    return {
      props: {
        fallbackData: collections,
      },
      revalidate: 240,
    };
  } catch (error) {
    console.error(error);
    return {
      props: {
        fallbackData: [],
      },
      revalidate: 1,
    };
  }
}

/**
 * SmartCollection
 *
 * A placeholder was added to prevent flashing between different collections based on the user state.
 * The placeholder and the spotlight components have a set size to prevent as much layout shift as possible.
 * This only affects the spotlight callout and the regular callout.  Any size changes made to either
 * the placeholder or spotlight components should be made to the other component as well.
 */
const SmartCollection = ({ collection, index, personalCollectionsLoaded }) => {
  const component = {
    id: collection.id,
    backgroundColor: collection.component_background_color,
    backgroundImage: collection.component_background,
    mobileBackgroundImage: collection.component_mobile_background,
    promoImage: collection.component_image,
    text: collection.component_promo,
    ctaText: collection.component_cta_text,
    ctaLink:
      collection.component_cta_link || `/collections/${collection.slug}/`,
  };
  let carouselComponent = null;

  switch (collection.component_type) {
    case 'thematic':
      return (
        <ThematicRoadblock
          key={collection.id}
          title={collection.title}
          image={collection.component_image}
          text={collection.component_promo}
          backgroundColor={collection.component_background_color}
          ctaText={collection.component_cta_text}
          ctaLink={
            collection.component_cta_link || `/collections/${collection.slug}`
          }
        />
      );

    case 'spotlight':
      carouselComponent = (
        <Spotlight
          key={collection.id}
          priority={index === 0}
          deals={collection.deals}
          component={component}
          title={collection.title}
          personalCollectionsLoaded={personalCollectionsLoaded}
        />
      );
      break;

    case 'spotlight_callout':
      carouselComponent = (
        <SpotlightCallout
          key={collection.id}
          priority={index === 0}
          deals={collection.deals}
          component={component}
          personalCollectionsLoaded={personalCollectionsLoaded}
        />
      );
      break;

    case 'product_carousel':
      return (
        <ProductCarousel
          id={collection.id}
          title={collection.title}
          deals={collection.deals}
          href={`/collections/${collection.slug}`}
        />
      );

    case 'countdown':
      return (
        <CountdownClock
          key={collection.id}
          startDate={collection.start_date}
          endDate={collection.end_date}
          promoImage={collection.component_image}
          backgroundImage={collection.component_background}
          mobileBackgroundImage={collection.component_mobile_background}
          ctaLink={collection.component_cta_link}
          ctaText={collection.component_cta_text}
          more={`/collections/${collection.slug}`}
        />
      );

    default:
      break;
  }

  if (!carouselComponent) {
    return null;
  }

  /**
   * If making changes to the sizing, be aware of cascading effects (see comment above)
   * The placeholder bg color should use the fallback collections color (if it exists)
   *
   * Notes:
   * - If we want to change the color of the loader based on the bg color we'll need to revist
   * - This placeholder only really works if there is a spotlight component in the fallback data in
   *   the same position as the spotlight in user's (or anon) collection data.  Otherwise there will
   *   be no placeholder or the page will "jump"
   */
  return (
    <div key={index}>
      {personalCollectionsLoaded ? (
        carouselComponent
      ) : (
        <div
          className="flex h-[686px] w-full items-center justify-center text-white sm:h-[778px] md:h-[804px] md:py-8 lg:h-[470px]"
          style={{
            backgroundColor: collection?.component_background_color ?? '#000',
          }}
        >
          <Loader size="7xl" className="" />
        </div>
      )}
    </div>
  );
};

export default function HomePage({ fallbackData }) {
  useOneTap();
  const { user } = useUser();
  const { collections, mutate } = useCollections(fallbackData);
  const [personalCollectionsLoaded, setPersonalCollectionsLoaded] =
    useState(false);

  // Add a fallback data timeout in case the user object is not populated
  useTimeout(() => setPersonalCollectionsLoaded(true), 8000);

  // refresh smart collections to accomodate A/B testing if they have changed
  useEffect(() => {
    // Wait for the user object to be populated before fetching smart collections
    // This is to prevent flashing between different collections based on the user state
    // Before making the request user will be undefined, anon users will be null
    if (typeof user !== 'undefined') {
      if (user?.id) {
        mutate().then(() => {
          setPersonalCollectionsLoaded(true);
        });
      } else {
        setPersonalCollectionsLoaded(true);
      }
    }
  }, [user, mutate]);

  const sortedCollections = useMemo(() => {
    return (
      collections
        .filter(
          (collection) =>
            !collection.query_parameters ||
            (collection.query_parameters && collection.deals?.length > 0) ||
            collection.component_type === 'countdown',
        )
        .sort((a, b) => b.weight - a.weight) ?? []
    );
  }, [collections]);

  // cache list of collections to render and splice in road block
  const componentsToRender = useMemo(() => {
    const components = sortedCollections
      .map((collection, index) => (
        <SmartCollection
          key={collection.id}
          index={index}
          collection={collection}
          personalCollectionsLoaded={personalCollectionsLoaded}
        />
      ))
      .filter(Boolean);

    return components;
  }, [sortedCollections, personalCollectionsLoaded]);

  return (
    <div className="mx-auto flex w-full max-w-[2000px] grow flex-col">
      {componentsToRender}
    </div>
  );
}
